/* eslint-disable no-restricted-syntax */
import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import session from 'containers/App/session';
import { Responsive, WidthProvider } from 'react-grid-layout';
import styled from 'styled-components';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Affix } from 'antd';
import Usage from './components/Usage/usage';

import messages from './messages';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import UnconstrainedForecast from 'containers/admin/AccountManagement/components/UnconstrainedForecast';
import LySpit from 'containers/admin/AccountManagement/components/LySpit';
import Recommendations from 'containers/admin/AccountManagement/components/Recommendations';
import Users from 'containers/admin/AccountManagement/components/Users';
import PropertyDetails from 'containers/admin/AccountManagement/components/PropertyDetails';
import UserFeatures from 'containers/admin/AccountManagement/components/UserFeatures';
import Alerts from 'containers/admin/AccountManagement/components/Alerts';

const Container = styled.div`
    overflow: auto;
    padding: 20px;
    min-width: 900px;
    > * {
        margin-bottom: 15px;
    }
`;

const Panel = styled.div`
    padding: 15px;
    background-color: white;
    border: 1px solid #eaeaea;
    box-shadow: 0 2px 4px 0 rgba(215, 215, 215, 0.5);
`;

const Title = styled.h2`
    font-family: ${({ theme }) => theme.font.title};
    color: ${({ theme }) => theme.colour.dark};
    text-align: left;
    margin-top: 5px;
    margin-bottom: 5px;
`;

const StatsItem = styled.div`
    border: 1px solid #eaeaea;
    border-radius: 2px;
    background-color: white;
    box-shadow: 0 4px 8px 0 rgba(215, 215, 215, 0.5);
    margin: 0;
`;

const ResponsiveReactGridLayout = WidthProvider(Responsive);

const initialLayouts = {
    lg: [
        {
            w: 4,
            h: 2,
            x: 0,
            y: 0,
            minW: 1,
            i: 'usage',
            moved: false,
            static: false,
        },
        {
            i: 'users',
            w: 2,
            h: 2,
            x: 5,
            y: 0,
            minW: 1,
            moved: false,
            static: false,
        },
        {
            i: 'unconstrainedForecast',
            w: 3,
            h: 2,
            x: 0,
            y: 2,
            minW: 1,
            moved: false,
            static: false,
        },
        {
            i: 'lySpit',
            w: 3,
            h: 2,
            x: 3,
            y: 2,
            minW: 1,
            moved: false,
            static: false,
        },
        {
            i: 'recommendations',
            w: 3,
            h: 2,
            x: 0,
            y: 4,
            minW: 1,
            moved: false,
            static: false,
        },
        {
            i: 'alerts',
            w: 3,
            h: 2,
            x: 3,
            y: 4,
            minW: 1,
            moved: false,
            static: false,
        },
        {
            i: 'propertyDetails',
            w: 4,
            h: 1,
            x: 0,
            y: 6,
            minW: 1,
            moved: false,
            static: false,
        },
        {
            i: 'userFeatures',
            w: 6,
            h: 2,
            x: 0,
            y: 7,
            minW: 2,
            moved: false,
            static: false,
        },
    ],
};

const StatisticsPage = () => {
    const propertyName = useSelector(session.selectors.selectPropertyName);
    const propertyId = useSelector(session.selectors.selectSelectedPropertyId);
    const token = useSelector(session.selectors.selectUserToken);
    const userDetails = useSelector(session.selectors.selectUserDetails);
    const isAdmin = userDetails.userId === 1;

    if (!isAdmin) {
        // remove propertyDetails and userFeatures from layouts
        initialLayouts.lg = initialLayouts.lg.filter(
            (item) => item.i !== 'propertyDetails' && item.i !== 'userFeatures' && item.i !== 'users',
        );
    }

    return (
        <Container>
            <Affix
                style={{
                    position: 'absolute',
                    top: 110,
                    right: 25,
                    cursor: 'pointer',
                    zIndex: 20,
                }}
            />
            <Panel>
                <Title>
                    <FormattedMessage {...messages.header} />
                </Title>
            </Panel>
            <ResponsiveReactGridLayout
                verticalCompact
                layouts={initialLayouts}
                rowHeight={280}
                breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                preventCollision={false}
                cols={{ lg: 6, md: 4, sm: 2, xs: 2, xxs: 2 }}
                autoSize
                draggableHandle=".draggable-item"
                margin={{
                    lg: [20, 20],
                    md: [20, 20],
                    sm: [20, 20],
                    xs: [20, 20],
                    xxs: [20, 20],
                }}
            >
                <StatsItem key="usage">
                    <Usage propertyName={propertyName} isDraggable />
                </StatsItem>
                <StatsItem key="alerts">
                    <Alerts propertyId={propertyId} individualPropertyID={propertyId} token={token} isDraggable />
                </StatsItem>
                <StatsItem key="unconstrainedForecast">
                    <UnconstrainedForecast individualPropertyID={propertyId} token={token} isDraggable />
                </StatsItem>
                <StatsItem key="lySpit">
                    <LySpit token={token} individualPropertyID={propertyId} isDraggable />
                </StatsItem>
                <StatsItem key="recommendations">
                    <Recommendations token={token} individualPropertyID={propertyId} isDraggable />
                </StatsItem>
                {isAdmin && (
                    <StatsItem key="users">
                        <Users token={token} individualPropertyID={propertyId} isDraggable />
                    </StatsItem>
                )}
                {isAdmin && (
                    <StatsItem key="propertyDetails">
                        <PropertyDetails token={token} individualPropertyID={propertyId} isDraggable />
                    </StatsItem>
                )}
                {isAdmin && (
                    <StatsItem key="userFeatures">
                        <UserFeatures token={token} individualPropertyID={propertyId} isDraggable />
                    </StatsItem>
                )}
            </ResponsiveReactGridLayout>
        </Container>
    );
};

StatisticsPage.propTypes = {
    // from react-intl
    intl: PropTypes.object.isRequired,
};

export default injectIntl(StatisticsPage);
